/**
 * Behavior Analysis Components
 * Visualizes user behavior data from PostHog
 */

import { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  FunnelChart,
  Funnel,
  LabelList,
  LineChart,
  Line,
  Legend,
  PieChart,
  Pie,
} from 'recharts';
import clsx from 'clsx';
import type {
  EventCount,
  FeatureUsageStats,
  ScreenViewStats,
  UserJourneyStep,
} from '@/lib/api/analytics';

// ============================================================
// Event Stats Components
// ============================================================

interface TopEventsChartProps {
  events: EventCount[];
  className?: string;
}

export function TopEventsChart({ events, className }: TopEventsChartProps) {
  const data = useMemo(() => {
    return events.slice(0, 10).map((e) => ({
      name: formatEventName(e.event),
      count: e.count,
      users: e.unique_users,
    }));
  }, [events]);

  return (
    <div className={className}>
      <h4 className="text-sm font-medium text-gray-600 mb-3">Top 10 事件</h4>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis type="number" stroke="#888" fontSize={12} />
            <YAxis
              type="category"
              dataKey="name"
              stroke="#888"
              fontSize={11}
              width={120}
              tick={{ fill: '#374151' }}
            />
            <Tooltip
              formatter={(value, name) => {
                const numValue = typeof value === 'number' ? value : 0;
                const label = name === 'count' ? '触发次数' : '独立用户';
                return [numValue.toLocaleString(), label];
              }}
            />
            <Legend />
            <Bar dataKey="count" name="触发次数" fill="#3b82f6" />
            <Bar dataKey="users" name="独立用户" fill="#10b981" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

interface DailyEventsChartProps {
  dailyStats: { date: string; total_events: number; unique_users: number }[];
  className?: string;
}

export function DailyEventsChart({ dailyStats, className }: DailyEventsChartProps) {
  const data = useMemo(() => {
    return [...dailyStats]
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((d) => ({
        date: d.date.slice(5), // MM-DD
        events: d.total_events,
        users: d.unique_users,
      }));
  }, [dailyStats]);

  return (
    <div className={className}>
      <h4 className="text-sm font-medium text-gray-600 mb-3">每日活跃趋势</h4>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="date" stroke="#888" fontSize={12} />
            <YAxis stroke="#888" fontSize={12} />
            <Tooltip />
            <Legend />
            <Line
              type="monotone"
              dataKey="events"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={{ fill: '#3b82f6', r: 3 }}
              name="事件数"
            />
            <Line
              type="monotone"
              dataKey="users"
              stroke="#10b981"
              strokeWidth={2}
              dot={{ fill: '#10b981', r: 3 }}
              name="活跃用户"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ============================================================
// Feature Usage Components
// ============================================================

interface FeatureUsageChartProps {
  features: FeatureUsageStats[];
  className?: string;
}

export function FeatureUsageChart({ features, className }: FeatureUsageChartProps) {
  const data = useMemo(() => {
    return features.slice(0, 10).map((f) => ({
      name: formatFeatureName(f.feature_name),
      clicks: f.click_count,
      users: f.unique_users,
    }));
  }, [features]);

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

  return (
    <div className={className}>
      <h4 className="text-sm font-medium text-gray-600 mb-3">功能使用分布</h4>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={80}
              dataKey="clicks"
              label={({ name, percent }) =>
                `${name} ${((percent ?? 0) * 100).toFixed(0)}%`
              }
              labelLine={false}
            >
              {data.map((_, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value) => {
                const numValue = typeof value === 'number' ? value : 0;
                return [numValue.toLocaleString(), '点击次数'];
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

interface FeatureUsageTableProps {
  features: FeatureUsageStats[];
  className?: string;
}

export function FeatureUsageTable({ features, className }: FeatureUsageTableProps) {
  return (
    <div className={clsx('overflow-x-auto', className)}>
      <h4 className="text-sm font-medium text-gray-600 mb-3">功能使用详情</h4>
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
              功能名称
            </th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
              点击次数
            </th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
              独立用户
            </th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
              平均时长
            </th>
          </tr>
        </thead>
        <tbody>
          {features.map((feature) => (
            <tr
              key={feature.feature_name}
              className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
            >
              <td className="px-4 py-3 text-sm text-gray-900">
                {formatFeatureName(feature.feature_name)}
              </td>
              <td className="px-4 py-3 text-sm text-gray-900">
                {feature.click_count.toLocaleString()}
              </td>
              <td className="px-4 py-3 text-sm text-gray-900">
                {feature.unique_users.toLocaleString()}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">
                {feature.avg_duration_ms
                  ? `${(feature.avg_duration_ms / 1000).toFixed(1)}s`
                  : '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================
// Screen Views Components
// ============================================================

interface ScreenViewsChartProps {
  screens: ScreenViewStats[];
  className?: string;
}

export function ScreenViewsChart({ screens, className }: ScreenViewsChartProps) {
  const data = useMemo(() => {
    return screens.slice(0, 10).map((s) => ({
      name: formatScreenName(s.screen_name),
      views: s.view_count,
      users: s.unique_users,
    }));
  }, [screens]);

  return (
    <div className={className}>
      <h4 className="text-sm font-medium text-gray-600 mb-3">页面浏览排行</h4>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis type="number" stroke="#888" fontSize={12} />
            <YAxis
              type="category"
              dataKey="name"
              stroke="#888"
              fontSize={11}
              width={100}
              tick={{ fill: '#374151' }}
            />
            <Tooltip
              formatter={(value, name) => {
                const numValue = typeof value === 'number' ? value : 0;
                const label = name === 'views' ? '浏览次数' : '独立用户';
                return [numValue.toLocaleString(), label];
              }}
            />
            <Legend />
            <Bar dataKey="views" name="浏览次数" fill="#8b5cf6" />
            <Bar dataKey="users" name="独立用户" fill="#f59e0b" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ============================================================
// User Journey Components
// ============================================================

interface UserJourneyFunnelProps {
  steps: UserJourneyStep[];
  className?: string;
}

export function UserJourneyFunnel({ steps, className }: UserJourneyFunnelProps) {
  const data = useMemo(() => {
    return steps.map((step, index) => ({
      name: step.step_name,
      value: step.users,
      fill: getFunnelColor(index),
      conversion: step.conversion_rate,
    }));
  }, [steps]);

  return (
    <div className={className}>
      <h4 className="text-sm font-medium text-gray-600 mb-3">用户旅程漏斗</h4>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <FunnelChart>
            <Tooltip
              formatter={(value, _name, props) => {
                const numValue = typeof value === 'number' ? value : 0;
                const conversion = props.payload?.conversion ?? 0;
                return [
                  `${numValue.toLocaleString()} 用户 (${conversion}%)`,
                  props.payload?.name,
                ];
              }}
            />
            <Funnel dataKey="value" data={data} isAnimationActive>
              <LabelList
                position="right"
                fill="#374151"
                stroke="none"
                dataKey="name"
              />
              <LabelList
                position="center"
                fill="#fff"
                stroke="none"
                formatter={(value) => {
                  const numValue = typeof value === 'number' ? value : 0;
                  return numValue.toLocaleString();
                }}
              />
            </Funnel>
          </FunnelChart>
        </ResponsiveContainer>
      </div>
      {/* Conversion table */}
      <div className="mt-4">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="px-3 py-2 text-left text-gray-500">步骤</th>
              <th className="px-3 py-2 text-left text-gray-500">用户数</th>
              <th className="px-3 py-2 text-left text-gray-500">转化率</th>
            </tr>
          </thead>
          <tbody>
            {steps.map((step) => (
              <tr key={step.step_name} className="border-b border-gray-100">
                <td className="px-3 py-2 text-gray-900">{step.step_name}</td>
                <td className="px-3 py-2 text-gray-900">
                  {step.users.toLocaleString()}
                </td>
                <td className="px-3 py-2">
                  <span
                    className={clsx(
                      'font-medium',
                      step.conversion_rate >= 50
                        ? 'text-green-600'
                        : step.conversion_rate >= 20
                        ? 'text-amber-600'
                        : 'text-red-600'
                    )}
                  >
                    {step.conversion_rate}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================
// Retention Components
// ============================================================

interface RetentionCardsProps {
  retention: Record<string, number>;
  className?: string;
}

export function RetentionCards({ retention, className }: RetentionCardsProps) {
  const retentionData = [
    { label: 'D1 留存', key: 'D1', description: '次日留存率' },
    { label: 'D7 留存', key: 'D7', description: '7日留存率' },
    { label: 'D30 留存', key: 'D30', description: '30日留存率' },
  ];

  return (
    <div className={clsx('grid grid-cols-3 gap-4', className)}>
      {retentionData.map(({ label, key, description }) => {
        const value = retention[key] ?? 0;
        return (
          <div
            key={key}
            className="bg-white rounded-lg p-4 border border-gray-200"
          >
            <div className="text-sm text-gray-500">{label}</div>
            <div
              className={clsx(
                'text-2xl font-bold mt-1',
                value >= 30
                  ? 'text-green-600'
                  : value >= 15
                  ? 'text-amber-600'
                  : 'text-red-600'
              )}
            >
              {value.toFixed(1)}%
            </div>
            <div className="text-xs text-gray-400 mt-1">{description}</div>
          </div>
        );
      })}
    </div>
  );
}

// ============================================================
// Summary Stats Components
// ============================================================

interface BehaviorSummaryCardsProps {
  summary: {
    total_events: number;
    unique_users: number;
    avg_events_per_user: number;
  };
  className?: string;
}

export function BehaviorSummaryCards({
  summary,
  className,
}: BehaviorSummaryCardsProps) {
  const cards = [
    {
      label: '总事件数',
      value: summary.total_events.toLocaleString(),
      subtitle: '行为事件',
    },
    {
      label: '活跃用户',
      value: summary.unique_users.toLocaleString(),
      subtitle: '独立用户',
    },
    {
      label: '人均事件',
      value: summary.avg_events_per_user.toFixed(1),
      subtitle: '平均每用户',
    },
  ];

  return (
    <div className={clsx('grid grid-cols-3 gap-4', className)}>
      {cards.map(({ label, value, subtitle }) => (
        <div
          key={label}
          className="bg-white rounded-lg p-4 border border-gray-200"
        >
          <div className="text-sm text-gray-500">{label}</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{value}</div>
          <div className="text-xs text-gray-400 mt-1">{subtitle}</div>
        </div>
      ))}
    </div>
  );
}

// ============================================================
// Helpers
// ============================================================

function formatEventName(event: string): string {
  const mapping: Record<string, string> = {
    app_opened: '打开App',
    chat_started: '开始聊天',
    chat_message_sent: '发送消息',
    feature_clicked: '功能点击',
    screen_viewed: '页面浏览',
    subscription_viewed: '查看订阅',
    soulmate_started: '开始Soulmate',
    '$pageview': '页面浏览',
    '$autocapture': '自动捕获',
  };
  return mapping[event] || event.replace(/_/g, ' ');
}

function formatFeatureName(name: string): string {
  const mapping: Record<string, string> = {
    soulmate: 'Soulmate',
    birth_chart: '命盘',
    daily_fortune: '每日运势',
    lucky_charm: '幸运符',
    settings: '设置',
    subscription: '订阅',
  };
  return mapping[name] || name.replace(/_/g, ' ');
}

function formatScreenName(name: string): string {
  return name.replace(/Screen$/, '').replace(/([A-Z])/g, ' $1').trim();
}

function getFunnelColor(index: number): string {
  const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
  return colors[index % colors.length];
}
